import * as PopoverPrimitive from '@radix-ui/react-popover';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { getQTipData } from '../Tooltip/qTip.utilities';
import type { ButtonWithPopoverProps } from './ButtonWithPopover.types';

const ButtonWithPopover = ({
  children,
  trigger,
  id,
  hasArrow,
  extraTriggerClassNames,
  extraPopoverClassNames,
  containerTestId,
  onInteractOutside,
  onPointerDownOutside,
  isTriggerAsChild = false,
  shouldTriggerFullWidth = false,
  disabled = false,
  align = 'end',
  alignOffset = -35,
  placement = 'bottom',
  placementOffset = 2,
  isHoverEnabled = false,
  hoverOpenDelay = 300,
  onOpenChange,
  isOpen,
  isCloseOnContentClick = false,
  isPortal = false,
  ...tooltipProps
}: ButtonWithPopoverProps) => {
  const tooltipData = getQTipData(tooltipProps);
  const timeout = useRef<null | NodeJS.Timeout>(null);
  const [isHoveredOpen, setIsHoveredOpen] = useState<boolean>(false);
  const clearHoverTimeout = () => {
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = null;
    }
  };

  const onHoverStart = () => {
    if (isHoverEnabled) {
      clearHoverTimeout();
      timeout.current = setTimeout(() => {
        setIsHoveredOpen(true);
        onOpenChange && onOpenChange(true);
      }, hoverOpenDelay);
    }
  };

  const onHoverEnd = () => {
    if (isHoverEnabled) {
      clearHoverTimeout();
      timeout.current = setTimeout(() => {
        setIsHoveredOpen(false);
        onOpenChange && onOpenChange(false);
      }, 200);
    }
  };

  useEffect(() => clearHoverTimeout, []);

  const renderContent = () => {
    return (
      <PopoverPrimitive.Content
        sideOffset={placementOffset}
        align={align}
        side={placement}
        onInteractOutside={onInteractOutside}
        onPointerDownOutside={onPointerDownOutside}
        alignOffset={isHoverEnabled ? -10 : alignOffset}
        asChild
        onClick={() => isCloseOnContentClick && onOpenChange && onOpenChange(false)}
        className="tw:h-full tw:w-full tw:outline-none tw:focus-visible:outline-none">
        <div
          data-testid={containerTestId}
          className={clsx(
            `tw:relative
            tw:z-1200
            tw:min-w-6
            tw:rounded-[0.5rem]
            tw:border
            tw:border-solid
            tw:border-sq-disabled-gray
            tw:bg-sq-white tw:text-sq-text-color tw:outline-none tw:focus-visible:outline-none
            tw:data-[side=bottom]:animate-slideUpAndFade
            tw:data-[side=left]:animate-slideRightAndFade
            tw:data-[side=right]:animate-slideLeftAndFade
            tw:data-[side=top]:animate-slideDownAndFade
            tw:dark:border-gray-500
            tw:dark:bg-sq-dark-background`,
            extraPopoverClassNames,
          )}>
          {hasArrow && (
            <PopoverPrimitive.Arrow asChild>
              <div
                className="
                  tw:-mt-sq-7 tw:h-sq-15 tw:w-sq-15 tw:rotate-45 tw:border-r
                  tw:border-b tw:border-sq-disabled-gray tw:bg-sq-white
                  tw:fill-transparent
                  tw:dark:border-gray-500 tw:dark:bg-sq-dark-background
                "
              />
            </PopoverPrimitive.Arrow>
          )}
          {children}
        </div>
      </PopoverPrimitive.Content>
    );
  };

  const renderPopover = (popoverOpenState?: boolean) => {
    return (
      <PopoverPrimitive.Root open={popoverOpenState} defaultOpen={false} onOpenChange={onOpenChange}>
        {trigger ? (
          <PopoverPrimitive.Trigger
            id={id}
            asChild={isTriggerAsChild}
            className={`
              tw:bg-transparent tw:outline-none
              tw:focus-visible:outline-none
              ${shouldTriggerFullWidth ? 'tw:h-full tw:w-full' : ''}
            `}
            disabled={disabled}>
            <div
              {...tooltipData}
              onMouseOver={onHoverStart}
              onMouseOut={onHoverEnd}
              className={clsx(
                `tw:outline-none
                 tw:focus-visible:outline-none
                 tw:dark:text-sq-white`,
                {
                  'tw:cursor-not-allowed tw:opacity-50': disabled,
                  'tw:flex tw:flex-col tw:items-center tw:justify-center': shouldTriggerFullWidth,
                  'tw:inline': !shouldTriggerFullWidth,
                },
                extraTriggerClassNames,
              )}>
              {trigger}
            </div>
          </PopoverPrimitive.Trigger>
        ) : (
          <PopoverPrimitive.Trigger asChild>
            <div />
          </PopoverPrimitive.Trigger>
        )}
        {isPortal ? <PopoverPrimitive.Portal>{renderContent()}</PopoverPrimitive.Portal> : renderContent()}
      </PopoverPrimitive.Root>
    );
  };

  return isHoverEnabled ? renderPopover(isHoveredOpen) : renderPopover(isOpen);
};

export default ButtonWithPopover;
